import { QuizStateType } from '@components/Quiz/state/state';
import { QuizActionType } from './actions';
import { OPEN_TO_MEDICATION } from '../../../types/quiz';

export const initReducerState = (props: QuizStateType): QuizStateType => props;

export type ReducerState = ReturnType<typeof initReducerState>;

/* eslint-disable complexity */
export function reducer(
  state: QuizStateType,
  action: QuizActionType,
): QuizStateType {
  switch (action.type) {
    case 'SET_PRIORITIES':
      return { ...state, priorities: action.payload };
    case 'SET_RECENT_CHALLENGES':
      return { ...state, recentChallenges: action.payload };
    case 'SET_PERSONAL_HABITS':
      return { ...state, personalHabits: action.payload };
    case 'SET_GOAL':
      return { ...state, goal: action.payload };
    case 'SET_GENDER':
      return { ...state, gender: action.payload };
    case 'SET_DATE_OF_BIRTH':
      return { ...state, dateOfBirth: action.payload };
    case 'SET_HEIGHT_UNIT':
      return { ...state, heightUnit: action.payload };
    case 'SET_HEIGHT':
      return {
        ...state,
        heightCm: action.payload.cm,
        heightFT: action.payload.ft,
        heightINCH: action.payload.inch,
      };
    case 'SET_WEIGHT_UNIT':
      return { ...state, weightUnit: action.payload };
    case 'SET_WEIGHT':
      return {
        ...state,
        weightKg: action.payload.kg,
        weightLBS: action.payload.lbs,
        weightST: action.payload.st,
      };
    case 'SET_HOW_DO_YOU_LIKE_YOUR_GOALS':
      return { ...state, howDoYouLikeYourGoals: action.payload };
    case 'SET_WEIGHT_GAIN_LIFE_EVENTS':
      return { ...state, weightGainLifeEvents: action.payload };
    case 'SET_HOW_IMPORTANT_IS_WEIGHT_TO_YOU':
      return { ...state, howImportantIsWeightToYou: action.payload };
    case 'SET_PREGNANCY':
      return { ...state, pregnancy: action.payload };
    case 'SET_DIABETES_STATUS':
      return { ...state, diabetesStatus: action.payload };
    case 'SET_DIABETES_MANAGEMENT':
      return { ...state, diabetesManagement: action.payload };
    case 'SET_HEALTH_CONDITIONS':
      return { ...state, healthConditions: action.payload };
    case 'SET_HEALTH_CONDITIONS_COMORBIDITIES': {
      const newHealthConditionsComorbidities = action.payload;

      // Filter out any details on this comorbidity that are no longer selected by the user
      const updatedHealthConditionsComorbiditiesDetails =
        state.healthConditionsComorbiditiesDetails.filter(comorbidity =>
          newHealthConditionsComorbidities.includes(comorbidity.key),
        );

      return {
        ...state,
        healthConditionsComorbidities: newHealthConditionsComorbidities,
        healthConditionsComorbiditiesDetails:
          updatedHealthConditionsComorbiditiesDetails,
      };
    }
    case 'SET_TYPE_2_DIABETES_MEDICATIONS':
      return { ...state, type2DiabetesMedications: action.payload };
    case 'SET_DIABETES_MEDICATION_PRESCRIBER':
      return { ...state, diabetesMedicationPrescriber: action.payload };
    case 'SET_HAS_DIABETES_DOCTORS_APPOINTMENT':
      return { ...state, hasDiabetesDoctorsAppointment: action.payload };
    case 'SET_DIABETES_COMPLICATIONS':
      return { ...state, diabetesComplications: action.payload };
    case 'SET_HAS_OTHER_HEALTH_CONDITIONS':
      return { ...state, hasOtherHealthConditions: action.payload };
    case 'SET_HAS_DIETARY_PREFERENCES':
      return { ...state, hasDietaryPreferences: action.payload };
    case 'SET_DIETARY_PREFERENCES':
      return { ...state, dietaryPreferences: action.payload };
    case 'SET_HAS_SUPPORT_SYSTEM':
      return { ...state, hasSupportSystem: action.payload };
    case 'SET_HEALTH_CONDITIONS_TEXT':
      return { ...state, healthConditionsText: action.payload };
    case 'SET_COMORBIDITY_DETAILS':
      return { ...state, healthConditionsComorbiditiesDetails: action.payload };
    case 'SET_TAKES_MEDICATION':
      return { ...state, takesMedication: action.payload };
    case 'SET_GOAL_WEIGHTS':
      return {
        ...state,
        goalWeightKg: action.payload.kg,
        goalWeightLBS: action.payload.lbs,
        goalWeightST: action.payload.st,
      };
    case 'SET_PERCENTAGE_GOAL':
      return { ...state, percentageGoals: action.payload };
    case 'SET_CURRENT_MEDICATION':
      return { ...state, currentMedication: action.payload };
    case 'SET_HAS_ALLERGIES':
      return { ...state, hasAllergies: action.payload };
    case 'SET_ALLERGY_DETAILS':
      return { ...state, allergyDetails: action.payload };
    case 'SET_OPEN_TO_MEDICATION':
      return { ...state, openToMedication: action.payload };
    case 'SET_ARE_YOU_A_VITALITY_MEMBER':
      return { ...state, areYouAVitalityMember: action.payload };
    case 'SET_HAS_WEIGHT_LOSS_GOAL':
      return { ...state, hasWeightLossGoal: action.payload };
    case 'SET_WEIGHT_LOSS_GOAL':
      return {
        ...state,
        weightLossGoalKg: action.payload.kg,
        weightLossGoalLBs: action.payload.lbs,
        weightLossGoalST: action.payload.st,
      };
    case 'HAS_STARTED_QUIZ':
      return {
        ...state,
        hasStartedQuiz: true,
      };
    case 'SET_FOOD_FEELINGS':
      return {
        ...state,
        foodFeelings: action.payload,
      };
    case 'SET_ALL':
      return { ...state, ...action.payload };
    case 'SET_WEIGHT_LOSS_APPROACH':
      return {
        ...state,
        weightLossApproach: action.payload,
      };
    case 'SET_HEALTHY_HABITS_SUPPORT':
      return {
        ...state,
        healthyHabitsSupport: action.payload,
      };
    case 'SET_BIGGEST_CONCERN':
      return { ...state, biggestConcern: action.payload };
    case 'SET_HABITS_ROLE':
      return { ...state, habitsRole: action.payload };
    case 'SET_LIFESTYLE_CHANGE':
      return { ...state, lifestyleChange: action.payload };
    case 'SET_LIFESTYLE_VISION':
      return { ...state, lifestyleVision: action.payload };
    /**
     * Update openToMedication in tandem with medicationView, so they don't fall
     * out of sync
     */
    case 'SET_MEDICATION_VIEW':
      return {
        ...state,
        medicationView: action.payload,
        openToMedication:
          action.payload === 'dontWantToUseMedication'
            ? OPEN_TO_MEDICATION.NO_MEDICATION
            : undefined,
      };
    case 'SET_PAST_EXPERIENCE':
      return { ...state, pastExperience: action.payload };
    case 'SET_PRIMARY_MOTIVATION':
      return { ...state, primaryMotivation: action.payload };
    case 'SET_SCIENCE_INTEREST':
      return { ...state, scienceInterest: action.payload };
    case 'SET_SUPPORT_TYPE':
      return { ...state, supportType: action.payload };
    case 'SET_TIMELINE':
      return { ...state, timeline: action.payload };
    case 'SET_ALCOHOL_CONSUMPTION':
      return { ...state, alcoholConsumption: action.payload };
    case 'SET_EATING_OUT_FREQUENCY':
      return { ...state, eatingOutFrequency: action.payload };
    case 'SET_EATING_PATTERN':
      return { ...state, eatingPattern: action.payload };
    case 'SET_LEISURE_TIME':
      return { ...state, leisureTime: action.payload };
    case 'SET_MEAL_PREPARATION_FREQUENCY':
      return { ...state, mealPreparationFrequency: action.payload };
    case 'SET_PHYSICAL_ACTIVITY_LEVEL':
      return { ...state, physicalActivityLevel: action.payload };
    case 'SET_SLEEP_HABITS':
      return { ...state, sleepHabits: action.payload };
    case 'SET_SOCIAL_SUPPORT':
      return { ...state, socialSupport: action.payload };
    case 'SET_STRESS_MANAGEMENT':
      return { ...state, stressManagement: action.payload };
    case 'SET_WORK_ENVIRONMENT_IMPACT':
      return { ...state, workEnvironmentImpact: action.payload };
    case 'SET_PRIMARY_ETHNICITY':
      return {
        ...state,
        primaryEthnicity: action.payload,
      };
    case 'SET_SECONDARY_ETHNICITY':
      return {
        ...state,
        secondaryEthnicity: action.payload,
      };
    case 'SET_RELIGION':
      return {
        ...state,
        religion: action.payload,
      };
    case 'SET_SELECTED_MEDICATION':
      return { ...state, selectedMedication: action.payload };
    case 'SET_FIRST_NAME':
      return { ...state, firstName: action.payload };
    case 'SET_LAST_NAME':
      return { ...state, lastName: action.payload };
    case 'SET_HAS_TAKEN_WEIGHT_LOSS_MEDICINE':
      return { ...state, hasTakenWeightLossMedicine: action.payload };
    case 'SET_WEIGHT_LOSS_MEDICINES_TAKEN':
      return { ...state, weightLossMedicinesTaken: action.payload };
    case 'SET_WHERE_HEARD_ABOUT_US':
      return { ...state, whereHeardAboutUs: action.payload };
    case 'SET_IS_MEDICAL_WORKER':
      return { ...state, isMedicalEmployee: action.payload };
    case 'SET_MEDICAL_WORKER_DETAILS':
      return { ...state, medicalWorkerDetails: action.payload };
    case 'SET_MOUNJARO_PROVIDER':
      return { ...state, mounjaroProvider: action.payload };
    case 'SET_MOUNJARO_LAST_DOSE':
      return { ...state, mounjaroLastDose: action.payload };
    case 'SET_MOUNJARO_LAST_DOSAGE':
      return { ...state, mounjaroLastDosage: action.payload };
    case 'SET_MOUNJARO_REASON_FOR_NEW_PROVIDER':
      return { ...state, mounjaroReasonForNewProvider: action.payload };
    default:
      throw new Error('Invalid action type');
  }
}
